/**
 * "תזכורת לטסט שנתי" — modal that opens when a user taps the test-expiry
 * push notification (or from any other trigger). Shows their plate, days
 * until tokef_dt expiry, and three actions: open testime.co.il for booking,
 * copy plate to clipboard (for pasting into testime), and open the
 * Ministry of Transport's personal area (for paying the license fee).
 *
 * Why no in-app booking: testime.co.il is the Ministry of Transport's
 * endorsed booking aggregator but has no public API. We can't programmatically
 * reserve a slot; the best UX is deep-link + clipboard handoff.
 *
 * Reads license status from the existing `license.service.js` so the modal
 * always reflects what the rest of the app sees (no double source of truth).
 */

import { getStatus as getLicenseStatus } from '../vehicle/license.service.js';
import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate, format as formatPlate } from '../../shared/plate.js';
import { escape } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('testReminder');

const OVERLAY_ID = 'testReminderOverlay';

/**
 * Open the test-reminder modal. If no plate is passed, falls back to the
 * currently signed-in user's vehicle from LocalState.
 *
 * @param {string} [plate]
 */
export async function open(plate) {
  ensureStyles();
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  const body = /** @type {HTMLElement} */ (overlay.querySelector('#testReminderBody'));

  body.innerHTML = `
    <div class="testrem-loading">
      <div class="testrem-spinner" aria-hidden="true"></div>
      <div class="testrem-loading-text">בודק תוקף הרישוי...</div>
    </div>
  `;
  overlay.classList.add('open');

  const resolvedPlate = normalizePlate(plate ?? LocalState.plate ?? '');
  if (!resolvedPlate) {
    body.innerHTML = `
      <div class="testrem-empty">
        <div class="testrem-empty-icon">🚗</div>
        <div class="testrem-empty-text">
          לא משויך לך רכב במערכת. כדי להפעיל תזכורות לטסט — שייך רכב לחשבון.
        </div>
      </div>
    `;
    return;
  }

  /** @type {Awaited<ReturnType<typeof getLicenseStatus>>} */
  let info;
  try {
    info = await getLicenseStatus(resolvedPlate);
  } catch (err) {
    log.warn('getLicenseStatus failed:', err);
    body.innerHTML = `
      <div class="testrem-empty">
        <div class="testrem-empty-icon">⚠️</div>
        <div class="testrem-empty-text">לא הצלחנו לטעון את נתוני הרישוי כרגע.</div>
      </div>
    `;
    return;
  }

  body.innerHTML = renderBody(resolvedPlate, info);
  wireActions(body, resolvedPlate);
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.classList.remove('open');
}

// ====================================================================
// Internals
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'testrem-overlay';
  overlay.innerHTML = `
    <aside class="testrem-modal" role="dialog" aria-modal="true" aria-labelledby="testRemTitle">
      <header class="testrem-header">
        <div id="testRemTitle" class="testrem-title">🗓️ תזכורת לטסט שנתי</div>
        <button class="testrem-close" id="testRemCloseBtn" type="button" aria-label="סגור">✕</button>
      </header>
      <div id="testReminderBody" class="testrem-body"></div>
      <footer class="testrem-footer">
        <button class="testrem-ok" id="testRemOkBtn" type="button">סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#testRemCloseBtn')?.addEventListener('click', close);
  overlay.querySelector('#testRemOkBtn')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Escape' && overlay?.classList.contains('open')) close();
  });
  return overlay;
}

/**
 * @param {string} plate
 * @param {{ status: string; daysLeft: number | null; tokefDate: Date | null; prettyDate: string | null }} info
 */
function renderBody(plate, info) {
  const { daysLeft: daysLeftRaw, prettyDate } = info;
  // license.service can return null when tokef can't be parsed; in that case
  // we don't have enough info to bucket, so render the "unknown" path.
  if (daysLeftRaw === null) {
    return `
      <div class="testrem-plate-wrap">
        <span class="testrem-plate-label">מספר רכב</span>
        <span class="testrem-plate">${escape(formatPlate(plate))}</span>
      </div>
      <div class="testrem-status testrem-status-soon">
        <div class="testrem-status-icon">📅</div>
        <div class="testrem-status-headline">לא נמצא תאריך תוקף</div>
        <div class="testrem-status-sub">לא הצלחנו לקרוא את תאריך פג התוקף ממשרד התחבורה. נסה לעדכן את פרטי הרכב.</div>
      </div>
    `;
  }
  const daysLeft = daysLeftRaw;
  /** @type {'expired' | 'urgent' | 'soon' | 'safe'} */
  let bucket;
  let headline;
  let sub;
  if (daysLeft < 0) {
    bucket = 'expired';
    headline = `הטסט פג לפני ${-daysLeft} ימים`;
    sub = 'נסיעה ברכב כעת אינה חוקית. הזמן תור מיד.';
  } else if (daysLeft <= 7) {
    bucket = 'urgent';
    headline = `הטסט פג בעוד ${daysLeft} ימים`;
    sub = 'דחוף — הזמן תור עוד היום כדי להימנע מקנס פיגורים.';
  } else if (daysLeft <= 30) {
    bucket = 'soon';
    headline = `הטסט פג בעוד ${daysLeft} ימים`;
    sub = 'זמן טוב להזמין תור — תורים פנויים יותר עכשיו מאשר ברגע האחרון.';
  } else {
    bucket = 'safe';
    headline = `הטסט תקף עוד ${daysLeft} ימים`;
    sub = 'אין דחיפות, אבל אפשר להזמין תור מראש לעוד מספר חודשים.';
  }

  return `
    <div class="testrem-plate-wrap">
      <span class="testrem-plate-label">מספר רכב</span>
      <span class="testrem-plate">${escape(formatPlate(plate))}</span>
    </div>

    <div class="testrem-status testrem-status-${bucket}">
      <div class="testrem-status-icon">${bucketIcon(bucket)}</div>
      <div class="testrem-status-headline">${escape(headline)}</div>
      <div class="testrem-status-sub">${escape(sub)}</div>
      ${prettyDate ? `<div class="testrem-status-date">תוקף עד ${escape(prettyDate)}</div>` : ''}
    </div>

    <div class="testrem-actions">
      <button class="testrem-action testrem-action-primary" data-act="open-testime">
        🌐 פתח testime.co.il להזמנת תור
        <span class="testrem-action-sub">המספר שלך יועתק ללוח אוטומטית — הדבק בטופס</span>
      </button>
      <button class="testrem-action" data-act="copy-plate">
        📋 העתק מספר רכב ללוח
        <span class="testrem-action-sub">${escape(formatPlate(plate))}</span>
      </button>
      <button class="testrem-action" data-act="open-gov">
        💼 פתח אזור אישי במשרד התחבורה
        <span class="testrem-action-sub">לתשלום אגרת רישוי / הצגת פרטים</span>
      </button>
    </div>

    <div class="testrem-note">
      💡 הזמנה דיגיטלית פעילה בכ־40 מכוני רישוי. אם המכון שלך לא ברשימה,
      התקשר אליו ישירות.
    </div>
  `;
}

/**
 * @param {HTMLElement} body
 * @param {string} plate
 */
function wireActions(body, plate) {
  const formattedPlate = formatPlate(plate);

  body.querySelector('[data-act="open-testime"]')?.addEventListener('click', () => {
    try { navigator.clipboard?.writeText(plate); } catch (_) { /* ignore */ }
    window.open('https://testime.co.il/', '_blank', 'noopener');
  });

  body.querySelector('[data-act="copy-plate"]')?.addEventListener('click', (e) => {
    const btn = /** @type {HTMLButtonElement} */ (e.currentTarget);
    try {
      navigator.clipboard?.writeText(plate);
      const orig = btn.innerHTML;
      btn.innerHTML = `✅ הועתק (${escape(formattedPlate)})<span class="testrem-action-sub">המספר ללוח</span>`;
      setTimeout(() => { btn.innerHTML = orig; }, 1500);
    } catch (_) { /* ignore */ }
  });

  body.querySelector('[data-act="open-gov"]')?.addEventListener('click', () => {
    window.open('https://www.gov.il/he/service/vehicle_license_payment', '_blank', 'noopener');
  });
}

/** @param {'expired' | 'urgent' | 'soon' | 'safe'} bucket */
function bucketIcon(bucket) {
  return ({ expired: '🚨', urgent: '⚠️', soon: '🟡', safe: '✅' })[bucket];
}

// ====================================================================
// Styles
// ====================================================================

let __stylesInjected = false;
function ensureStyles() {
  if (__stylesInjected) return;
  __stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .testrem-overlay {
      position: fixed; inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      z-index: 99998;
      display: none;
      align-items: center; justify-content: center;
      padding: 20px;
      font-family: 'Rubik', 'Heebo', sans-serif;
    }
    .testrem-overlay.open { display: flex; }
    .testrem-modal {
      background: #fff;
      border-radius: 20px;
      width: 100%; max-width: 440px; max-height: 92vh;
      display: flex; flex-direction: column;
      direction: rtl; overflow: hidden;
      box-shadow: 0 30px 80px rgba(0,0,0,0.40);
      animation: testrem-pop 0.22s ease-out;
    }
    @keyframes testrem-pop {
      from { transform: scale(0.94); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    .testrem-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 18px;
      border-bottom: 1px solid #e2e8f0;
    }
    .testrem-title { font-weight: 800; font-size: 16px; color: #0f172a; }
    .testrem-close {
      background: transparent; border: none;
      font-size: 22px; color: #64748b;
      cursor: pointer; line-height: 1; padding: 4px 8px;
    }
    .testrem-close:hover { color: #0f172a; }
    .testrem-body { padding: 18px; overflow-y: auto; flex: 1; }
    .testrem-footer {
      padding: 12px 18px 16px;
      border-top: 1px solid #e2e8f0;
      display: flex; justify-content: center;
    }
    .testrem-ok {
      padding: 11px 36px;
      border: none; border-radius: 999px;
      background: linear-gradient(135deg, #2DA88A, #258A72);
      color: #fff; font-family: inherit;
      font-weight: 800; font-size: 14px;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(45,168,138,0.30);
    }

    .testrem-loading, .testrem-empty {
      display: flex; flex-direction: column; align-items: center;
      gap: 14px; padding: 30px 0;
    }
    .testrem-empty-icon { font-size: 38px; }
    .testrem-empty-text { font-size: 13px; color: #475569; text-align: center; line-height: 1.5; }
    .testrem-spinner {
      width: 30px; height: 30px;
      border: 3px solid #e2e8f0;
      border-top-color: #2DA88A;
      border-radius: 50%;
      animation: testrem-spin 0.8s linear infinite;
    }
    @keyframes testrem-spin { to { transform: rotate(360deg); } }
    .testrem-loading-text { color: #64748b; font-size: 13px; }

    .testrem-plate-wrap {
      display: flex; flex-direction: column; align-items: center;
      gap: 4px; margin-bottom: 16px;
    }
    .testrem-plate-label {
      font-size: 11px; color: #64748b; font-weight: 600;
      letter-spacing: 0.05em; text-transform: uppercase;
    }
    .testrem-plate {
      display: inline-block;
      background: #fef3c7; color: #0f172a;
      padding: 8px 16px; border-radius: 8px;
      font-family: 'Courier New', monospace;
      font-weight: 900; font-size: 22px;
      letter-spacing: 0.08em;
      border: 2px solid #f59e0b;
      direction: ltr;
    }

    .testrem-status {
      padding: 20px 18px; border-radius: 14px;
      text-align: center; margin-bottom: 18px;
    }
    .testrem-status-icon { font-size: 38px; margin-bottom: 8px; line-height: 1; }
    .testrem-status-headline { font-weight: 900; font-size: 20px; margin-bottom: 6px; }
    .testrem-status-sub { font-size: 13px; opacity: 0.85; line-height: 1.5; }
    .testrem-status-date {
      margin-top: 10px;
      font-size: 12px; opacity: 0.85;
      font-family: 'Courier New', monospace;
    }
    .testrem-status-expired { background: linear-gradient(135deg,#fee2e2,#fecaca); color: #991b1b; }
    .testrem-status-urgent  { background: linear-gradient(135deg,#fef3c7,#fde68a); color: #78350f; }
    .testrem-status-soon    { background: linear-gradient(135deg,#fef9c3,#fef08a); color: #713f12; }
    .testrem-status-safe    { background: linear-gradient(135deg,#d1fae5,#a7f3d0); color: #065f46; }

    .testrem-actions {
      display: flex; flex-direction: column; gap: 10px;
      margin-bottom: 14px;
    }
    .testrem-action {
      width: 100%; text-align: right;
      padding: 12px 14px;
      background: linear-gradient(135deg,#f1f5f9,#e2e8f0);
      border: 1px solid #cbd5e1; border-radius: 10px;
      font-family: inherit; cursor: pointer;
      display: flex; flex-direction: column; gap: 3px;
      transition: transform 0.12s;
    }
    .testrem-action:hover { transform: translateY(-1px); background: linear-gradient(135deg,#e2e8f0,#cbd5e1); }
    .testrem-action > :first-child { font-weight: 800; font-size: 13.5px; color: #0f172a; }
    .testrem-action-sub { font-size: 11.5px; color: #64748b; font-weight: 500; }
    .testrem-action-primary {
      background: linear-gradient(135deg,#2DA88A,#258A72);
      border-color: #1C6E5B;
    }
    .testrem-action-primary > :first-child { color: #fff; }
    .testrem-action-primary .testrem-action-sub { color: rgba(255,255,255,0.85); }
    .testrem-action-primary:hover { background: linear-gradient(135deg,#258A72,#1C6E5B); }

    .testrem-note {
      padding: 11px 14px;
      background: #f0f9ff;
      border-radius: 10px;
      font-size: 11.5px; color: #075985;
      line-height: 1.5;
    }
  `;
  document.head.appendChild(style);
}
